import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader, EmptyState, StatCard } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { PlusCircle, Wallet, Play, Printer, Trash2, FileSpreadsheet, Receipt, Upload, Mail, Loader2, Building2, Landmark, FileText, Coins } from 'lucide-react';
import { fmtINR, monthName } from '@/lib/format';
import { useNavigate } from 'react-router-dom';
import LoansTab from './PayrollLoans';

function EmployeeDialog({ companyId, employee, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    name: '', email: '', employee_code: '', role: '', pan: '', bank_account: '', bank_ifsc: '', bank_name: '',
    basic: 30000, hra: 15000, special_allowance: 12000, other_allowance: 3000, bonus: 0,
    annual_ctc: 720000, pf_applicable: true, esi_applicable: false, tds_regime: 'new', pt_applicable: true, active: true,
  });

  useEffect(() => {
    if (employee) {
      setF({
        ...employee,
        basic: employee.salary_structure?.basic || 0,
        hra: employee.salary_structure?.hra || 0,
        special_allowance: employee.salary_structure?.special_allowance || 0,
        other_allowance: employee.salary_structure?.other_allowance || 0,
        bonus: employee.salary_structure?.bonus || 0,
      });
    }
  }, [employee]);

  const submit = async () => {
    const payload = {
      company_id: companyId,
      name: f.name, email: f.email, employee_code: f.employee_code, role: f.role, pan: f.pan,
      bank_account: f.bank_account, bank_ifsc: f.bank_ifsc, bank_name: f.bank_name,
      salary_structure: { basic: +f.basic, hra: +f.hra, special_allowance: +f.special_allowance, other_allowance: +f.other_allowance, bonus: +f.bonus },
      annual_ctc: +f.annual_ctc, pf_applicable: !!f.pf_applicable, esi_applicable: !!f.esi_applicable,
      tds_regime: f.tds_regime, pt_applicable: !!f.pt_applicable, active: !!f.active,
    };
    try {
      if (employee) await api.patch(`/payroll/employees/${employee.id}`, payload);
      else await api.post('/payroll/employees', payload);
      toast.success('Saved'); setOpen(false); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{employee ? <Button size="sm" variant="ghost" data-testid={`edit-employee-${employee.id}`}>Edit</Button> : <Button data-testid="add-payroll-employee"><PlusCircle className="h-4 w-4 mr-2" />Add employee</Button>}</DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{employee ? 'Edit employee' : 'Add payroll employee'}</DialogTitle><DialogDescription>Salary structure, statutory flags and bank details for India payroll.</DialogDescription></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="emp-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Employee code</Label><Input value={f.employee_code} onChange={(e) => setF({ ...f, employee_code: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Role</Label><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>PAN</Label><Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Bank name</Label><Input value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Bank account</Label><Input value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>IFSC</Label><Input value={f.bank_ifsc} onChange={(e) => setF({ ...f, bank_ifsc: e.target.value })} /></div>

          <div className="md:col-span-2 pt-2 border-t"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Salary structure (monthly)</p></div>
          <div className="space-y-1.5"><Label>Basic</Label><Input type="number" value={f.basic} onChange={(e) => setF({ ...f, basic: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>HRA</Label><Input type="number" value={f.hra} onChange={(e) => setF({ ...f, hra: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Special allowance</Label><Input type="number" value={f.special_allowance} onChange={(e) => setF({ ...f, special_allowance: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Other allowance</Label><Input type="number" value={f.other_allowance} onChange={(e) => setF({ ...f, other_allowance: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Bonus</Label><Input type="number" value={f.bonus} onChange={(e) => setF({ ...f, bonus: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Annual CTC</Label><Input type="number" value={f.annual_ctc} onChange={(e) => setF({ ...f, annual_ctc: e.target.value })} /></div>

          <div className="md:col-span-2 pt-2 border-t"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Statutory flags</p></div>
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">PF applicable</span><Switch checked={f.pf_applicable} onCheckedChange={(v) => setF({ ...f, pf_applicable: v })} /></label>
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">ESI applicable</span><Switch checked={f.esi_applicable} onCheckedChange={(v) => setF({ ...f, esi_applicable: v })} /></label>
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">Professional tax</span><Switch checked={f.pt_applicable} onCheckedChange={(v) => setF({ ...f, pt_applicable: v })} /></label>
          <div className="space-y-1.5"><Label>TDS regime</Label><Select value={f.tds_regime} onValueChange={(v) => setF({ ...f, tds_regime: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">New (FY 25-26)</SelectItem><SelectItem value="old">Old</SelectItem></SelectContent></Select></div>
          <label className="flex items-center justify-between rounded-lg border p-2 md:col-span-2"><span className="text-sm">Active</span><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></label>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="emp-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PayrollPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tab, setTab] = useState('employees');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [running, setRunning] = useState(false);

  const load = async () => {
    const [e, r] = await Promise.all([
      api.get(`/payroll/employees?company_id=${companyId}`),
      api.get(`/payroll/runs?company_id=${companyId}`),
    ]);
    setEmployees(e.data); setRuns(r.data);
  };
  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); load(); }, [companyId]);

  const del = async (id) => { if (!window.confirm('Delete employee?')) return; await api.delete(`/payroll/employees/${id}`); load(); };

  const runPayroll = async () => {
    setRunning(true);
    try {
      const r = await api.post('/payroll/runs', { company_id: companyId, month: +month, year: +year });
      toast.success(`Payroll finalized • Net payout ${fmtINR(r.data.totals.net_pay)}`);
      setTab('runs'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    finally { setRunning(false); }
  };

  const delRun = async (id) => { if (!window.confirm('Delete this payroll run?')) return; await api.delete(`/payroll/runs/${id}`); load(); };

  const totalSalary = employees.reduce((s, e) => s + ((e.salary_structure?.basic||0) + (e.salary_structure?.hra||0) + (e.salary_structure?.special_allowance||0) + (e.salary_structure?.other_allowance||0) + (e.salary_structure?.bonus||0)), 0);

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · Payroll (India)`}
        description="Employee salary structures, PF/ESI/TDS calculations, printable payslips and bank payment reports."
        actions={<div className="flex items-center gap-2"><ImportEmployeesButton companyId={companyId} onImported={load} /><EmployeeDialog companyId={companyId} onSaved={load} /></div>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total employees" value={employees.length} icon={Wallet} tone="primary" />
          <StatCard label="Active" value={employees.filter((e) => e.active).length} icon={Wallet} tone="good" />
          <StatCard label="Est. monthly outgo" value={fmtINR(totalSalary)} icon={Receipt} />
          <StatCard label="Payroll runs" value={runs.length} icon={FileSpreadsheet} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="payroll-tabs">
            <TabsTrigger value="employees">Employees</TabsTrigger>
            <TabsTrigger value="monthly">Monthly editor</TabsTrigger>
            <TabsTrigger value="loans" data-testid="tab-loans"><Coins className="h-3.5 w-3.5 mr-1" />Loans & advances</TabsTrigger>
            <TabsTrigger value="run">Run payroll</TabsTrigger>
            <TabsTrigger value="runs">Runs & reports</TabsTrigger>
          </TabsList>
          <TabsContent value="employees" className="mt-4">
            {employees.length === 0 ? (
              <EmptyState icon={Wallet} title="No employees yet" description="Add employees to run payroll for this company." action={<EmployeeDialog companyId={companyId} onSaved={load} />} />
            ) : (
              <div className="rounded-xl border bg-card overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Employee</TableHead><TableHead>Code</TableHead><TableHead className="text-right">Basic</TableHead><TableHead className="text-right">Gross</TableHead><TableHead>PF/ESI</TableHead><TableHead>Regime</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {employees.map((e) => {
                      const gross = (e.salary_structure?.basic||0) + (e.salary_structure?.hra||0) + (e.salary_structure?.special_allowance||0) + (e.salary_structure?.other_allowance||0) + (e.salary_structure?.bonus||0);
                      return (
                        <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                          <TableCell><p className="font-medium">{e.name}</p><p className="text-xs text-muted-foreground">{e.role}</p></TableCell>
                          <TableCell>{e.employee_code || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtINR(e.salary_structure?.basic)}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{fmtINR(gross)}</TableCell>
                          <TableCell><span className="text-xs text-muted-foreground">{e.pf_applicable ? 'PF' : ''}{e.pf_applicable && e.esi_applicable ? ' · ' : ''}{e.esi_applicable ? 'ESI' : ''}{!e.pf_applicable && !e.esi_applicable ? '—' : ''}</span></TableCell>
                          <TableCell><span className="text-xs capitalize">{e.tds_regime}</span></TableCell>
                          <TableCell><span className={`text-xs px-2 py-0.5 rounded ${e.active ? 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]' : 'bg-muted text-muted-foreground'}`}>{e.active ? 'active' : 'inactive'}</span></TableCell>
                          <TableCell className="flex items-center gap-1">
                            <EmployeeDialog companyId={companyId} employee={e} onSaved={load} />
                            <Button size="icon" variant="ghost" onClick={() => del(e.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="monthly" className="mt-4">
            <MonthlyEditor companyId={companyId} employees={employees} />
          </TabsContent>

          <TabsContent value="loans" className="mt-4">
            <LoansTab companyId={companyId} employees={employees} />
          </TabsContent>

          <TabsContent value="run" className="mt-4">
            <Card className="p-5 max-w-2xl">
              <h3 className="font-display font-semibold">Run monthly payroll</h3>
              <p className="text-sm text-muted-foreground">Compute PF, ESI, TDS and net payout for every active employee. Finalized runs generate printable payslips and bank reports.</p>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="space-y-1"><Label className="text-xs">Month</Label><Select value={String(month)} onValueChange={(v) => setMonth(+v)}><SelectTrigger data-testid="payroll-month"><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: 12 }).map((_, i) => <SelectItem key={i+1} value={String(i+1)}>{monthName(i+1)}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1"><Label className="text-xs">Year</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="payroll-year" /></div>
              </div>
              <Button className="mt-4" onClick={runPayroll} disabled={running || employees.length === 0} data-testid="payroll-run-button">
                {running ? 'Running…' : <><Play className="h-4 w-4 mr-2" />Run payroll</>}
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="runs" className="mt-4">
            {runs.length === 0 ? (
              <EmptyState icon={FileSpreadsheet} title="No payroll runs yet" description="Run your first payroll to see printable reports and payslips." />
            ) : (
              <div className="space-y-3">
                {runs.map((r) => (
                  <Card key={r.id} className="p-4" data-testid={`payroll-run-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-display font-semibold">{monthName(r.month)} {r.year}</p>
                        <p className="text-xs text-muted-foreground">Generated by {r.generated_by_name || '—'} on {new Date(r.generated_at).toLocaleString()}</p>
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-3">
                          <div><p className="text-[10px] uppercase text-muted-foreground">Employees</p><p className="tabular-nums font-medium">{r.entries.length}</p></div>
                          <div><p className="text-[10px] uppercase text-muted-foreground">Gross</p><p className="tabular-nums font-medium">{fmtINR(r.totals.gross)}</p></div>
                          <div><p className="text-[10px] uppercase text-muted-foreground">PF</p><p className="tabular-nums">{fmtINR(r.totals.pf_employee)}</p></div>
                          <div><p className="text-[10px] uppercase text-muted-foreground">ESI</p><p className="tabular-nums">{fmtINR(r.totals.esi_employee)}</p></div>
                          <div><p className="text-[10px] uppercase text-muted-foreground">TDS</p><p className="tabular-nums">{fmtINR(r.totals.tds)}</p></div>
                          <div><p className="text-[10px] uppercase text-muted-foreground">Net payout</p><p className="tabular-nums font-semibold text-primary">{fmtINR(r.totals.net_pay)}</p></div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => window.open(`/payroll/print/bank/${r.id}`, '_blank')} data-testid={`print-bank-${r.id}`}><Printer className="h-3.5 w-3.5 mr-1" />Bank report</Button>
                        <PayrollEmailDialog runId={r.id} company={company} bankMode={false} />
                        <PayrollEmailDialog runId={r.id} company={company} bankMode={true} />
                        <Button size="sm" variant="ghost" onClick={() => delRun(r.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    </div>
                    <div className="mt-3 rounded-lg border overflow-hidden">
                      <RunEntriesEditor run={r} onSaved={load} />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function ImportEmployeesButton({ companyId, onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/payroll/employees/import?company_id=${companyId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${r.data.created} employees${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      onImported && onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <div className="inline-flex">
      <input ref={inputRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files[0])} />
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="payroll-import-excel">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Import from Excel
      </Button>
    </div>
  );
}

function MonthlyEditor({ companyId, employees }) {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);  // employee_id -> {overtime, extra_allowance, extra_deduction, lop_days}
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ovs, lvs, pv] = await Promise.all([
        api.get(`/payroll/overrides?company_id=${companyId}&month=${month}&year=${year}`),
        api.get(`/payroll/leaves?company_id=${companyId}&month=${month}&year=${year}`),
        api.post('/payroll/preview', { company_id: companyId, month: +month, year: +year }).catch(() => null),
      ]);
      const ovMap = Object.fromEntries((ovs.data || []).map((o) => [o.employee_id, o]));
      const lvMap = Object.fromEntries((lvs.data || []).map((l) => [l.employee_id, l]));
      setRows(employees.map((e) => ({
        employee_id: e.id, name: e.name, employee_code: e.employee_code, role: e.role,
        overtime_amount: ovMap[e.id]?.overtime_amount || 0,
        extra_allowance: ovMap[e.id]?.extra_allowance || 0,
        extra_deduction: ovMap[e.id]?.extra_deduction || 0,
        lop_days: lvMap[e.id]?.lop_days || 0,
        leaves_taken: lvMap[e.id]?.leaves_taken || 0,
      })));
      setPreview(pv?.data || null);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (employees.length) load(); }, [companyId, month, year, employees.length]);

  const setField = (empId, field, val) => setRows(rows.map((r) => r.employee_id === empId ? { ...r, [field]: val } : r));

  const saveRow = async (r) => {
    try {
      await Promise.all([
        api.post('/payroll/overrides', { company_id: companyId, employee_id: r.employee_id, month: +month, year: +year, overtime_amount: +r.overtime_amount || 0, extra_allowance: +r.extra_allowance || 0, extra_deduction: +r.extra_deduction || 0 }),
        api.post('/payroll/leaves', { company_id: companyId, employee_id: r.employee_id, month: +month, year: +year, leaves_taken: +r.leaves_taken || 0, lop_days: +r.lop_days || 0 }),
      ]);
      toast.success(`Saved for ${r.name}`);
      load();
    } catch (e) { toast.error('Save failed'); }
  };

  const saveAll = async () => {
    setLoading(true);
    try {
      for (const r of rows) await saveRow(r);
      toast.success('All rows saved');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="space-y-1"><Label className="text-xs">Month</Label><Select value={String(month)} onValueChange={(v) => setMonth(+v)}><SelectTrigger className="w-40 h-9" data-testid="me-month"><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: 12 }).map((_, i) => <SelectItem key={i+1} value={String(i+1)}>{monthName(i+1)}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs">Year</Label><Input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="w-28 h-9" data-testid="me-year" /></div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="me-refresh">{loading ? 'Loading…' : 'Refresh'}</Button>
          <Button size="sm" onClick={saveAll} disabled={loading} data-testid="me-save-all">Save all</Button>
          {preview && <p className="ml-auto text-sm text-muted-foreground">Preview net payout: <span className="text-foreground font-medium">{fmtINR(preview.totals.net_pay)}</span></p>}
        </div>
      </Card>
      {employees.length === 0 ? <EmptyState icon={Wallet} title="Add employees first" description="Add or import employees to use the monthly editor." /> : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead>
              <TableHead className="text-right">Overtime (₹)</TableHead>
              <TableHead className="text-right">Extra allowance</TableHead>
              <TableHead className="text-right">Extra deduction</TableHead>
              <TableHead className="text-right">Leaves</TableHead>
              <TableHead className="text-right">LoP days</TableHead>
              <TableHead className="text-right">Preview net</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => {
                const pvEntry = preview?.entries?.find((e) => e.employee_id === r.employee_id);
                return (
                  <TableRow key={r.employee_id} data-testid={`me-row-${r.employee_id}`}>
                    <TableCell><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.role || '—'} · {r.employee_code || '—'}</p></TableCell>
                    <TableCell><Input type="number" className="h-8 text-right w-28" value={r.overtime_amount} onChange={(e) => setField(r.employee_id, 'overtime_amount', e.target.value)} data-testid={`me-overtime-${r.employee_id}`} /></TableCell>
                    <TableCell><Input type="number" className="h-8 text-right w-28" value={r.extra_allowance} onChange={(e) => setField(r.employee_id, 'extra_allowance', e.target.value)} data-testid={`me-extra-all-${r.employee_id}`} /></TableCell>
                    <TableCell><Input type="number" className="h-8 text-right w-28" value={r.extra_deduction} onChange={(e) => setField(r.employee_id, 'extra_deduction', e.target.value)} data-testid={`me-extra-ded-${r.employee_id}`} /></TableCell>
                    <TableCell><Input type="number" className="h-8 text-right w-20" value={r.leaves_taken} onChange={(e) => setField(r.employee_id, 'leaves_taken', e.target.value)} data-testid={`me-leaves-${r.employee_id}`} /></TableCell>
                    <TableCell><Input type="number" className="h-8 text-right w-20" value={r.lop_days} onChange={(e) => setField(r.employee_id, 'lop_days', e.target.value)} data-testid={`me-lop-${r.employee_id}`} /></TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{pvEntry ? fmtINR(pvEntry.net_pay) : '—'}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => saveRow(r)} data-testid={`me-save-${r.employee_id}`}>Save</Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function PayrollEmailDialog({ runId, company, bankMode }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState((company?.directors || []).map((d) => d.email).filter(Boolean).join(', '));
  const [cc, setCc] = useState((company?.default_cc_emails || []).join(', '));
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/payroll/runs/${runId}/email`, {
        to: to.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        cc: cc.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        message: msg, include_bank_only: bankMode,
      });
      const st = r.data.result?.status;
      if (st === 'sent') toast.success('Sent');
      else if (st === 'queued') toast.message('Queued as draft (SMTP not configured)');
      else toast.error('Failed');
      setOpen(false);
    } catch (e) { toast.error('Failed'); }
    finally { setSending(false); }
  };

  const Icon = bankMode ? Landmark : Building2;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`email-${bankMode ? 'bank' : 'company'}-${runId}`}><Mail className="h-3.5 w-3.5 mr-1" /><Icon className="h-3.5 w-3.5 mr-1" />{bankMode ? 'Email bank' : 'Email company'}</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{bankMode ? 'Email bank payment advice' : 'Email payroll to company'}</DialogTitle><DialogDescription>{bankMode ? 'Only name, bank details and net amount.' : 'Full payroll breakdown with PF/ESI/TDS.'}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} data-testid={`email-to-${bankMode ? 'bank' : 'co'}`} /></div>
          <div className="space-y-1"><Label>CC</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} /></div>
          <div className="space-y-1"><Label>Message</Label><Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditableAmount({ value, onSave, testId }) {
  const [v, setV] = useState(value ?? 0);
  const [editing, setEditing] = useState(false);
  useEffect(() => setV(value ?? 0), [value]);
  const commit = async () => {
    if (+v === +value) { setEditing(false); return; }
    await onSave(+v);
    setEditing(false);
  };
  return editing ? (
    <div className="flex items-center justify-end gap-1">
      <Input type="number" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} className="h-7 w-24 text-right tabular-nums text-xs" autoFocus data-testid={testId} />
    </div>
  ) : (
    <button onClick={() => setEditing(true)} className="w-full text-right tabular-nums hover:bg-muted/50 rounded px-1 py-0.5" data-testid={`${testId}-edit`}>{fmtINR(value)}</button>
  );
}

function RunEntriesEditor({ run, onSaved }) {
  const [entries, setEntries] = useState(run.entries || []);
  const [totals, setTotals] = useState(run.totals || {});
  useEffect(() => { setEntries(run.entries || []); setTotals(run.totals || {}); }, [run]);

  const patch = async (employee_id, field, value) => {
    try {
      const r = await api.patch(`/payroll/runs/${run.id}/entry`, { run_id: run.id, employee_id, field, value });
      setEntries(r.data.entries); setTotals(r.data.totals);
      toast.success('Updated & recomputed');
      onSaved && onSaved();
    } catch (e) { toast.error('Save failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Editable salary segments — click any amount to edit before emailing/printing</p>
        <p className="text-xs text-muted-foreground">Net pay recomputes automatically</p>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Employee</TableHead>
          <TableHead className="text-right">Gross</TableHead>
          <TableHead className="text-right">Overtime</TableHead>
          <TableHead className="text-right">Extra allow.</TableHead>
          <TableHead className="text-right">Extra ded.</TableHead>
          <TableHead className="text-right">PF</TableHead>
          <TableHead className="text-right">ESI</TableHead>
          <TableHead className="text-right">TDS</TableHead>
          <TableHead className="text-right">PT</TableHead>
          <TableHead className="text-right"><span title="Loan / advance EMI"><Coins className="h-3 w-3 inline mr-0.5" />Loan</span></TableHead>
          <TableHead className="text-right">Net</TableHead>
          <TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {entries.map((en) => (
            <TableRow key={en.employee_id}>
              <TableCell><p className="font-medium">{en.name}</p><p className="text-xs text-muted-foreground">{en.email}</p></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.gross} testId={`edit-gross-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'gross', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.overtime_amount || 0} testId={`edit-ot-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'overtime_amount', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.extra_allowance || 0} testId={`edit-xa-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'extra_allowance', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.extra_deduction || 0} testId={`edit-xd-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'extra_deduction', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.pf_employee} testId={`edit-pf-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'pf_employee', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.esi_employee} testId={`edit-esi-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'esi_employee', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.tds} testId={`edit-tds-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'tds', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.professional_tax} testId={`edit-pt-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'professional_tax', v)} /></TableCell>
              <TableCell className="text-right"><EditableAmount value={en.loan_deduction || 0} testId={`edit-loan-${en.employee_id}`} onSave={(v) => patch(en.employee_id, 'loan_deduction', v)} /></TableCell>
              <TableCell className="text-right tabular-nums font-medium">{fmtINR(en.net_pay)}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => window.open(`/payroll/print/payslip/${run.id}/${en.employee_id}`, '_blank')} data-testid={`print-payslip-${run.id}-${en.employee_id}`} title="Download / print payslip"><FileText className="h-3.5 w-3.5" /></Button></TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-muted/40 font-medium">
            <TableCell>Totals</TableCell>
            <TableCell className="text-right tabular-nums">{fmtINR(totals.gross)}</TableCell>
            <TableCell colSpan={3} />
            <TableCell className="text-right tabular-nums">{fmtINR(totals.pf_employee)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtINR(totals.esi_employee)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtINR(totals.tds)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtINR(totals.professional_tax)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtINR(totals.loan_deduction || 0)}</TableCell>
            <TableCell className="text-right tabular-nums text-primary">{fmtINR(totals.net_pay)}</TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

